/**
 * Settings.
 *
 * Your own row at the top, then the categories.
 *
 * The categories are the ones every source agreed on — Account, Privacy,
 * Chats, Notifications, Storage and data, with Help at the bottom.
 * Searching turned up little else that was consistent: the guides
 * contradicted each other on where App language and Invite a friend live,
 * and the newest report describes a redesign that renames this to a "You"
 * tab and moves the categories behind a gear icon. That is a test, so it
 * is not what this follows.
 *
 * Most rows have nothing behind them yet and say so rather than opening an
 * empty page. Theme and sign out work, because both are real here.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDisplay } from '@zivapp/shared'
import { useAuth, useTheme } from '../context/index.js'
import Avatar from '../components/ui/Avatar.jsx'
import './Settings.css'

const CATEGORIES = [
  {
    id: 'account',
    label: 'Account',
    hint: 'Security notifications, change number, delete account',
    icon: 'M12 15.2a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM3.6 21v-.8a6 6 0 0 1 6-6h4.8a6 6 0 0 1 6 6v.8'
  },
  {
    id: 'privacy',
    label: 'Privacy',
    hint: 'Blocked contacts, last seen, read receipts',
    icon: 'M12 2.8 20 6v6c0 4.6-3.2 8-8 9.2C8 20 4.8 16.6 4.8 12V6Z'
  },
  {
    id: 'chats',
    label: 'Chats',
    hint: 'Theme, wallpaper, chat history',
    icon: 'M5 4h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H9l-5 4v-4a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z'
  },
  {
    id: 'notifications',
    label: 'Notifications',
    hint: 'Message, group and call tones',
    icon: 'M18 16.5v-5a6 6 0 1 0-12 0v5l-2 2h16ZM10 21a2.2 2.2 0 0 0 4 0'
  },
  {
    id: 'storage',
    label: 'Storage and data',
    hint: 'Network usage, auto-download',
    icon: 'M4 7.5c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3ZM4 7.5v9c0 1.7 3.6 3 8 3s8-1.3 8-3v-9'
  },
  {
    id: 'help',
    label: 'Help',
    hint: 'Help centre, contact us, terms and privacy policy',
    icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9.6 9.4a2.5 2.5 0 1 1 3.4 2.3c-.7.3-1 .9-1 1.6v.4M12 17.2v.2'
  }
]

export default function Settings () {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { preference, isDark, isFollowingSystem, setTheme } = useTheme()
  const [signingOut, setSigningOut] = useState(false)

  const themeLabel = isFollowingSystem ? 'System' : (preference === 'dark' ? 'Dark' : 'Light')
  const nextTheme = isFollowingSystem ? 'light' : (preference === 'light' ? 'dark' : null)

  return (
    <div className="st">
      <header className="st__bar">
        <h1>Settings</h1>
      </header>

      <div className="st__scroll">
        <button className="st__me" type="button" onClick={() => navigate('/profile')}>
          <Avatar name={user?.displayName} url={user?.avatarUrl} size={62} />
          <span className="st__me-body">
            <span className="st__me-name">{user?.displayName || 'Set your name'}</span>
            <span className="st__me-about">
              {user?.about || (user?.phone ? formatDisplay(user.phone) : '')}
            </span>
          </span>
          <svg className="st__chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Theme is here rather than buried under Chats because it is the
            one preference in this list that actually does something. */}
        <ul className="st__list">
          <li>
            <button className="st__row" type="button" onClick={() => setTheme(nextTheme)}>
              <span className="st__icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M12 3.5a8.5 8.5 0 0 1 0 17Z" fill="currentColor" />
                </svg>
              </span>
              <span className="st__row-body">
                <span className="st__row-label">Theme</span>
                <span className="st__row-hint">
                  {themeLabel}{isFollowingSystem && ` · currently ${isDark ? 'dark' : 'light'}`}
                </span>
              </span>
              <span className="st__value">{themeLabel}</span>
            </button>
          </li>

          {CATEGORIES.map(c => (
            <li key={c.id}>
              <button className="st__row" type="button" disabled title="Not built yet">
                <span className="st__icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d={c.icon} stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="st__row-body">
                  <span className="st__row-label">{c.label}</span>
                  <span className="st__row-hint">{c.hint}</span>
                </span>
                <span className="st__soon">Not built</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="st__out">
          <button
            className="st__signout"
            type="button"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true)
              await signOut()
              // onAuthChange moves the app on; this only avoids a double tap.
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v2"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M10 12h11m0 0-3.5-3.5M21 12l-3.5 3.5" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>
    </div>
  )
}
